/** Read side of the `window.__federaShell` bridge, re-rendering on `federa-shell-updated`. WRITE side: `useFederaShellSync.ts` -- the hook that actually publishes the bridge, not the ambient `federa-shell-global.ts` this line used to name. */

import { useSyncExternalStore } from 'react';
import { globalKey } from '@federa/const/global-namespace';
import { stringOrNull } from '@federa/const/string-guards';
import { isAuthorizedForMissionControl } from '@federa/utils/auth/role-normalization-core';
import { isBrowser } from '@federa/utils/browser/detection';
import { DOM_EVENTS } from '@federa/const/websocket-actions-dom';
import type { ShellIdentity } from '@federa/types/mfe-bridge';

export function subscribeToShell(onUpdate: () => void): () => void {
	window.addEventListener(DOM_EVENTS.FEDERA_SHELL_UPDATED, onUpdate);
	return () => window.removeEventListener(DOM_EVENTS.FEDERA_SHELL_UPDATED, onUpdate);
}

interface ShellUserBridge {
	user?: { isLoggedIn?: boolean; userId?: string; rawUserId?: string; username?: string; name?: string; role?: string; phoneNumberOtp?: string };
	/** Whether the HOST's sign-in authority has settled; published beside the user by `useFederaShellSync`. */
	auth?: { isAuthInitialized?: boolean };
	/** The host app's SPA router push, published by ShellContextBridge. */
	navigate?: (path: string) => void;
	/** The host app's full route prefetch, published beside it. */
	prefetch?: (path: string) => void;
	/** The host app's sign-out, published only by a host whose hosted desk carries the way out (`FederaShell.logout`). */
	logout?: () => Promise<void>;
}

/** `Reflect.get` reaches the runtime-composed key without widening `Window`; `declare global` cannot serve because `globalKey()` is a runtime call and an ambient name must be a literal TYPE. */
function readShellBridge(): ShellUserBridge | undefined {
	return Reflect.get(window, globalKey('Shell')) as ShellUserBridge | undefined;
}

export function getShellUserId(): string | null {
	const user = readShellBridge()?.user;
	return user?.isLoggedIn === true ? stringOrNull(user.rawUserId) : null;
}

/** A command the shell published, or null when its slot holds no function -- the bridge is read off `window`, so the
 *  slot is checked at run time rather than trusted. Null, never a no-op: each reader below says why it needs that. */
function publishedCommand<T extends (...args: never[]) => unknown>(command: T | undefined): T | null {
	return typeof command === 'function' ? command : null;
}

/** Null rather than a no-op is load-bearing: the caller decides whether to `preventDefault`, and a standalone SPA must fall through to a REAL navigation. */
export function getShellNavigate(): ((path: string) => void) | null {
	if (!isBrowser()) return null;
	return publishedCommand(readShellBridge()?.navigate);
}

/** The host router's full prefetch, or null when no shell publishes one (a standalone SPA has no route cache to warm). */
export function getShellPrefetch(): ((path: string) => void) | null {
	if (!isBrowser()) return null;
	return publishedCommand(readShellBridge()?.prefetch);
}

/**
 * The host's sign-out command, or null when no shell publishes one -- and NULL IS THE ANSWER, never a no-op: a hosted
 * desk draws its Log out control only where pressing it does something. The `admin-app-state` shells (the admin
 * console, Noor, Kenecting) publish it through their one shared stack (`FederaShell.logout`, 2026-09-18); the federa
 * shell publishes none, and a standalone SPA origin has no bridge at all.
 */
export function getShellLogout(): (() => Promise<void>) | null {
	if (!isBrowser()) return null;
	return publishedCommand(readShellBridge()?.logout);
}

export function getShellRole(): string | null {
	return readShellBridge()?.user?.role ?? null;
}

/** Null server snapshot so an MFE rendering under SSR cannot throw a hydration mismatch; every MFE mounts client-side, so it is a no-op live. */
export function useShellUserId(): string | null {
	return useSyncExternalStore(subscribeToShell, getShellUserId, () => null);
}

/** Hook form of {@link getShellRole}, re-rendering on `federa-shell-updated`. Null server snapshot for the same
 *  SSR-hydration reason as {@link useShellUserId}. */
export function useShellRole(): string | null {
	return useSyncExternalStore(subscribeToShell, getShellRole, () => null);
}

/**
 * Is the shell user authorized for the G4+ (mission-control) surfaces?
 *
 * GATED ON THE RESOLVED ID, never on the role alone. `getShellUserId` is the one place that tests `isLoggedIn` AND
 * type-guards `rawUserId`, so a role read without it authorizes a subject the shell has not resolved -- a partially
 * published bridge (role present, login not yet confirmed) reads as G4. The navigator MFE carried the ungated spelling
 * and the profile SPA the gated one; the gated form is promoted here because the two disagreed on exactly that case.
 */
export function getShellIsG4(): boolean {
	if (getShellUserId() === null) return false;
	return isAuthorizedForMissionControl(getShellRole());
}

/** Hook form of {@link getShellIsG4}, re-rendering on `federa-shell-updated`. The `false` server snapshot is the parity
 *  of `useShellUserId`'s null one -- neither MFE spelling declared one, and `useSyncExternalStore` throws under SSR without it. */
export function useShellIsG4(): boolean {
	return useSyncExternalStore(subscribeToShell, getShellIsG4, () => false);
}

/** THE SETTLED IDENTITY'S SHAPE (`ShellIdentity`) is derived from the bridge's own contract in `types/mfe-bridge.ts`
 *  (homed there 2026-09-22, QUAL-TYPE-SSOT-001), read by the trade MFE and the identity sync hook too. */

/** Snapshot cache keyed by the bridge OBJECT: the writer re-assigns the bridge once per sync, so an unchanged bridge
 *  answers the same identity object -- the stability `useSyncExternalStore` requires of a snapshot. */
let shellIdentityCache: { bridge: ShellUserBridge | undefined; identity: ShellIdentity | null } = { bridge: undefined, identity: null };

/**
 * The host's signed-in identity, or null when no shell is published. The host shell mirrors its OWN sign-in authority
 * (its `AuthProvider`, through `AuthStateSyncer`) onto the bridge, and every bearer an embedded bundle sends is minted
 * from that same host session -- so this, and never a cookie, is what an embedded root's store may call its user.
 */
export function getShellIdentity(): ShellIdentity | null {
	if (!isBrowser()) return null;
	const bridge = readShellBridge();
	if (bridge === shellIdentityCache.bridge) return shellIdentityCache.identity;
	const user = bridge?.user;
	const identity: ShellIdentity | null =
		bridge === undefined
			? null
			: {
					userId: user?.isLoggedIn === true ? (stringOrNull(user.userId) ?? '') : '',
					rawUserId: user?.isLoggedIn === true ? (stringOrNull(user.rawUserId) ?? '') : '',
					username: stringOrNull(user?.username) ?? '',
					name: stringOrNull(user?.name) ?? '',
					role: stringOrNull(user?.role) ?? '',
					phoneNumberOtp: user?.isLoggedIn === true ? (stringOrNull(user.phoneNumberOtp) ?? '') : '',
					isAuthInitialized: bridge.auth?.isAuthInitialized === true,
				};
	shellIdentityCache = { bridge, identity };
	return identity;
}

/** Hook form of {@link getShellIdentity}, re-rendering on `federa-shell-updated`. Null server snapshot, as the siblings. */
export function useShellIdentity(): ShellIdentity | null {
	return useSyncExternalStore(subscribeToShell, getShellIdentity, () => null);
}

/** Hook form of {@link getShellLogout}, re-rendering on `federa-shell-updated` -- the bridge is re-assigned as one
 *  object per sync, so the function identity is a stable snapshot between syncs. Null server snapshot as the siblings. */
export function useShellLogout(): (() => Promise<void>) | null {
	return useSyncExternalStore(subscribeToShell, getShellLogout, () => null);
}
